import {Fragment} from 'react';

import styled from 'styled-components';

import {SkeletonLoader} from '~/shared/components/Loaders';
import {getLocalizationService} from '~/shared/services/localisationService';
import {DETAILS_TYPES} from '~/shared/consts/userReportConsts';
import {media} from '~/shared/theme/media';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import TenbisIcon from '~/shared/utils/payments/cardsPie/moneycardSmall.svg?react';

import PrepaidCard from '../../PrepaidCard';

import CreditSection from './CreditSection';
import {MobileLoaderComponent} from './LoaderComponents';
import MoneyCardDetail from './MoneyCardDetail';
import {MobileInnerWrapper, Title, Wrapper} from './shared.styles';

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  ${({withTopMargin}) => withTopMargin && 'margin-top: 8px;'}
  ${media.minLargeTablet`
    margin-top: 36px;
    padding: 0 16px;
  `}
`;

const CardTitle = styled(Title)`
  margin-top: 0;
  ${flipOnLTR`
    margin-right: 8px;
  `}
`;

const CardDeleted = styled(Title)`
  color: ${({theme}) => theme.colors.error};
  ${flipOnLTR`margin-right: 8px;`}
`;

const CardDeletedLine = styled(Title)`
  ${flipOnLTR`margin-right: 8px;`}
`;

const Icon = styled(TenbisIcon)`
  margin-bottom: 19px;
`;

export const MobileMoneyCardDetails = ({moneyCardsList, reportSetting, isIntelReport, setOrderListData, onAutoCreditSubscribeSuccess}) => {
  const {t} = getLocalizationService();
  return (
    <SkeletonLoader shouldShowLoader={!moneyCardsList} LoaderComponent={MobileLoaderComponent}>
      <Wrapper>
        {moneyCardsList?.map((moneyCard, index) => {
          const {
            cardSuffix,
            limitation,
            usage,
            balance,
            usageBruto,
            usageNet,
            personalGuestTotal,
            businessMeetingGuestTotal,
            moneycardId,
            tenbisCreditConversion,
            isTenbisCredit,
            isPrepaid,
            cardDeleted,
            encryptedMoneycardId,
            isActivated,
          } = moneyCard;

          if (!isActivated && !cardDeleted) {
            return null;
          }

          return (
            <Fragment key={cardSuffix}>
              {isPrepaid && (
                <PrepaidCard isTenbisCredit={isTenbisCredit} balance={balance.monthly} cardSuffix={cardSuffix} />
              )}
              {!isTenbisCredit && !isPrepaid && (
                <>
                  <Header withTopMargin={!!index}>
                    <Icon />
                    <CardTitle as="h1">{t('card_number_xxxx', {cardSuffix})}</CardTitle>
                    {cardDeleted && (
                    <>
                      <CardDeletedLine>-</CardDeletedLine>
                      <CardDeleted>{t('cancelled_card')}</CardDeleted>
                    </>
                )}
                  </Header>
                  <MobileInnerWrapper>
                    {!isIntelReport && (
                      <MoneyCardDetail
                        isMobile
                        daily={!limitation.hideDailyLimit && {sum: limitation.daily, text: t('daily_limitation')}}
                        weekly={limitation.showWeeklyLimit && {sum: limitation.weekly, text: t('weekly_limitation')}}
                        monthly={
                          !limitation.hideMonthlyLimit && {
                            sum: limitation.monthly,
                            text: t('monthly_limitation'),
                          }
                        }
                        type={DETAILS_TYPES.LIMITATION}
                      />
                    )}

                    <MoneyCardDetail
                      isMobile
                      daily={!usage.hideDailyLimit && {sum: usage.daily, text: t('used_today')}}
                      weekly={usage.showWeeklyLimit && {sum: usage.weekly, text: t('used_this_week')}}
                      monthly={
                        !usage.hideMonthlyLimit && {
                          sum: usage.monthly,
                          text: t('used_this_month'),
                        }
                      }
                      type={DETAILS_TYPES.USAGE}
                    />

                    {!isIntelReport && (
                      <MoneyCardDetail
                        isMobile
                        daily={
                          !reportSetting.hideDailyBalanceInUserReport &&
                          !balance.hideDailyLimit && {sum: balance.daily, text: t('daily_balance')}
                        }
                        weekly={balance.showWeeklyLimit && {sum: balance.weekly, text: t('weekly_balance')}}
                        monthly={
                          !balance.hideMonthlyLimit && {
                            sum: balance.monthly,
                            text: t('monthly_balance'),
                          }
                        }
                        type={DETAILS_TYPES.BALANCE}
                      />
                    )}

                    {isIntelReport && (
                      <>
                        <MoneyCardDetail
                          isMobile
                          daily={
                            !reportSetting.hideDailyBalanceInUserReport &&
                            !usageBruto.hideDailyLimit && {sum: usageBruto.daily, text: t('daily_bruto')}
                          }
                          weekly={usageBruto.showWeeklyLimit && {sum: usageBruto.weekly, text: t('weekly_bruto')}}
                          monthly={
                            !usageBruto.hideMonthlyLimit && {
                              sum: usageBruto.monthly,
                              text: t('monthly_bruto'),
                            }
                          }
                          type={DETAILS_TYPES.GROSS}
                        />
                        <MoneyCardDetail
                          isMobile
                          daily={
                            !reportSetting.hideDailyBalanceInUserReport &&
                            !usageNet.hideDailyLimit && {sum: usageNet.daily, text: t('daily_nett')}
                          }
                          weekly={usageNet.showWeeklyLimit && {sum: usageNet.weekly, text: t('weekly_nett')}}
                          monthly={
                            !usageNet.hideMonthlyLimit && {
                              sum: usageNet.monthly,
                              text: t('monthly_nett'),
                            }
                          }
                          type={DETAILS_TYPES.NETT}
                        />
                        <MoneyCardDetail
                          isMobile
                          daily={{sum: personalGuestTotal, text: t('personal_guest')}}
                          monthly={{sum: businessMeetingGuestTotal, text: t('business_guest')}}
                          type={DETAILS_TYPES.GUEST}
                        />
                      </>
                    )}
                  </MobileInnerWrapper>

                  <CreditSection
                    availableAmount={tenbisCreditConversion?.availableAmount}
                    moneycardId={moneycardId?.toString()}
                    setOrderListData={setOrderListData}
                    onAutoCreditSubscribeSuccess={onAutoCreditSubscribeSuccess}
                    tenbisCreditConversion={tenbisCreditConversion}
                    encryptedMoneycardId={encryptedMoneycardId}
                  />
                </>
              )}
            </Fragment>
          );
        })}
      </Wrapper>
    </SkeletonLoader>
  );
};
